import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Icon from '../../../components/AppIcon';
import { API_ENDPOINTS } from '../../../utils/config';
import Image from '../../../components/AppImage';

const ActivityFeed = () => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchRecentActivities();
  }, []);

  const fetchRecentActivities = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Accept': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      // Fetch recent properties and clients
      const [propertiesRes, clientsRes] = await Promise.all([
        axios.get(API_ENDPOINTS.PROPERTY_LIST, { headers }).catch(() => ({ data: [] })),
        axios.get(API_ENDPOINTS.CLIENTS_LIST, { headers }).catch(() => ({ data: [] }))
      ]);

      const propertiesData = Array.isArray(propertiesRes.data) 
        ? propertiesRes.data 
        : (propertiesRes.data?.results || propertiesRes.data?.data || propertiesRes.data?.properties || []);

      const clientsData = Array.isArray(clientsRes.data) 
        ? clientsRes.data 
        : (clientsRes.data?.results || clientsRes.data?.data || clientsRes.data?.clients || []);

      // Get last 5 recently added properties (sorted by created_at descending)
      const recentProperties = propertiesData
        .sort((a, b) => new Date(b.created_at || b.createdAt) - new Date(a.created_at || a.createdAt))
        .slice(0, 5);

      // Map to activities
      const mappedActivities = recentProperties.map((property, index) => {
        const client = clientsData.find(c => c.id === property.client_id || c.client_id === property.client_id);
        const address = property.property_address || property.address || 'Unknown address';
        const propertyType = property.property_type || property.propertyType || 'Property';
        
        return {
          id: property.id || index + 1,
          type: "property_added",
          title: "Property Added",
          description: `${propertyType} at ${address} has been added to the system`,
          client: client?.name || client?.client_name || property?.customer_name || property?.bank_name || 'Unknown Client',
          property: {
            address,
            image: property.image_url || property.image || '/assets/images/no_image.png'
          },
          timestamp: new Date(property.created_at || property.createdAt || Date.now()),
          status: (property.status || "active").toString().toLowerCase().replace(/\s+/g, '_'),
          surveyor: property.assigned_to || "System"
        };
      });

      setActivities(mappedActivities);
    } catch (error) {
      // Failed to fetch activities handled silently
      setActivities([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'CheckCircle';
      case 'pending': return 'Clock';
      case 'scheduled': return 'Calendar';
      case 'in_progress': return 'Activity';
      default: return 'Circle';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'var(--color-success)';
      case 'pending': return 'var(--color-warning)';
      case 'scheduled': return 'var(--color-accent)';
      case 'in_progress': return 'var(--color-primary)';
      default: return 'var(--color-muted-foreground)';
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor(diff / (1000 * 60));
    
    if (hours > 0) return `${hours}h ago`;
    return `${minutes}m ago`;
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Icon name="Activity" size={20} color="var(--color-primary)" />
          <h3 className="text-lg font-semibold text-foreground">Recent Activity</h3>
        </div>
        <button
          onClick={() => navigate('/property-management')}
          className="text-sm text-accent hover:text-accent/80 font-medium"
        >
          View All
        </button>
      </div>
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : activities?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Icon name="Inbox" size={48} color="var(--color-muted-foreground)" />
          <p className="text-muted-foreground mt-4">No recent activities</p>
        </div>
      ) : (
        <div className="space-y-4">
          {activities?.map((activity) => (
          <div key={activity?.id} className="flex items-start space-x-4 p-4 rounded-lg hover:bg-muted/50 transition-smooth">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 rounded-lg overflow-hidden">
                <Image 
                  src={activity?.property?.image} 
                  alt={activity?.property?.address}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <Icon 
                  name={getStatusIcon(activity?.status)} 
                  size={16} 
                  color={getStatusColor(activity?.status)} 
                />
                <h4 className="text-sm font-medium text-foreground truncate">
                  {activity?.title}
                </h4>
              </div>
              
              <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                {activity?.description}
              </p>
              
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <div className="flex items-center space-x-4">
                  <span className="flex items-center space-x-1">
                    <Icon name="User" size={12} />
                    <span>{activity?.client}</span>
                  </span>
                  <span className="flex items-center space-x-1">
                    <Icon name="MapPin" size={12} />
                    <span className="truncate max-w-32">{activity?.property?.address}</span>
                  </span>
                </div>
                <span>{formatTimeAgo(activity?.timestamp)}</span>
              </div>
            </div>
          </div>
        ))}
        </div>
      )}
    </div>
  );
};

export default ActivityFeed;